/**
 * SamplePicker — UI para seleccionar entre múltiples variaciones generadas
 * Optimizado para mostrar 1-3 samples con indicador de progreso
 */
import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Check, Loader2, Sparkles } from 'lucide-react';

export function SamplePicker({ 
  groupId, 
  samples = [], 
  onSelect, 
  onPollGroup,
  isPolling = false,
  className = '' 
}) {
  const [playing, setPlaying] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const audioRefs = useRef({});

  // Poll group status periodically
  useEffect(() => {
    if (!groupId || !onPollGroup) return;
    
    const allDone = samples.every(s => s.status === 'done' || s.status === 'error');
    if (allDone) return;
    
    const interval = setInterval(() => {
      onPollGroup(groupId);
    }, 2500);
    
    return () => clearInterval(interval);
  }, [groupId, samples, onPollGroup]);

  const togglePlay = (taskId, audioUrl) => {
    // Pause all others
    Object.entries(audioRefs.current).forEach(([id, audio]) => {
      if (id !== taskId && audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    if (playing === taskId) {
      audioRefs.current[taskId]?.pause();
      setPlaying(null);
    } else {
      if (!audioRefs.current[taskId]) {
        audioRefs.current[taskId] = new Audio(audioUrl);
        audioRefs.current[taskId].onended = () => setPlaying(null);
      }
      audioRefs.current[taskId].play();
      setPlaying(taskId);
    }
  };

  const handleSelect = (taskId) => {
    setSelectedId(taskId);
    // Pause all
    Object.values(audioRefs.current).forEach(a => a?.pause());
    setPlaying(null);
    onSelect?.(taskId);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach(a => a?.pause());
    };
  }, []);

  const completedCount = samples.filter(s => s.status === 'done').length;
  const totalCount = samples.length;

  if (samples.length === 0) {
    return null;
  }

  // Single sample mode — just show loading/done state
  if (samples.length === 1) {
    const sample = samples[0];
    if (sample.status === 'generating') {
      return (
        <div className={`flex items-center gap-2 text-[#F5C842] text-xs ${className}`}>
          <Loader2 size={14} className="animate-spin" />
          <span>Generando audio...</span>
        </div>
      );
    }
    return null; // Single sample auto-selects, no picker needed
  }

  return (
    <div className={`bg-[#0A0A0B] rounded-xl border border-[#27272A] p-2.5 sm:p-3 ${className}`} data-testid="sample-picker">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Sparkles size={12} className="text-[#F5C842]" />
          <span className="text-[11px] sm:text-xs font-medium text-white">
            {completedCount === totalCount ? 'Elige tu variación favorita' : 'Generando variaciones...'}
          </span>
        </div>
        <span className="text-[9px] sm:text-[10px] text-[#52525B]">
          {completedCount}/{totalCount} listas
        </span>
      </div>

      {/* Progress bar */}
      {completedCount < totalCount && (
        <div className="w-full h-1 bg-[#1E1E23] rounded-full mb-2 overflow-hidden">
          <div 
            className="h-full bg-[#F5C842] transition-all duration-500"
            style={{ width: `${(completedCount / totalCount) * 100}%` }}
          />
        </div>
      )}

      {/* Sample cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
        {samples.map((sample, idx) => {
          const isReady = sample.status === 'done';
          const isError = sample.status === 'error';
          const isPlaying = playing === sample.task_id;
          const isSelected = selectedId === sample.task_id;
          
          const labels = ['Original', 'Energética', 'Atmosférica'];
          const colors = ['#F5C842', '#FF6B00', '#00D4FF'];

          return (
            <div
              key={sample.task_id}
              data-testid={`sample-card-${idx}`}
              className={`
                relative rounded-lg border-2 transition-all overflow-hidden
                ${isSelected 
                  ? 'border-[#F5C842] bg-[#F5C84215]' 
                  : isReady 
                    ? 'border-[#27272A] hover:border-[#3F3F46] bg-[#0F0F12]'
                    : 'border-[#1E1E23] bg-[#0F0F12] opacity-60'
                }
              `}
            >
              {/* Label */}
              <div 
                className="px-2 py-1 text-[8px] sm:text-[9px] font-bold uppercase tracking-wider"
                style={{ 
                  background: `${colors[idx]}15`,
                  color: colors[idx],
                }}
              >
                {labels[idx] || `Variación ${idx + 1}`}
              </div>

              {/* Content */}
              <div className="p-2 sm:p-2.5">
                {!isReady && !isError && (
                  <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3">
                    <Loader2 size={16} className="animate-spin text-[#52525B]" />
                    <span className="text-[9px] sm:text-[10px] text-[#52525B]">Generando...</span>
                  </div>
                )}

                {isError && (
                  <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3">
                    <span className="text-[9px] sm:text-[10px] text-red-400">Error al generar</span>
                  </div>
                )}

                {isReady && (
                  <div className="flex items-center gap-2">
                    {/* Play button */}
                    <button
                      onClick={() => togglePlay(sample.task_id, sample.audio_url)}
                      className={`
                        w-8 sm:w-10 h-8 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0
                        transition-all
                        ${isPlaying 
                          ? 'bg-[#F5C842] text-black' 
                          : 'bg-[#27272A] text-white hover:bg-[#3F3F46]'
                        }
                      `}
                      data-testid={`sample-play-${idx}`}
                    >
                      {isPlaying ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                    </button>

                    {/* Waveform placeholder */}
                    <div className="flex-1 h-6 sm:h-8 flex items-center gap-0.5">
                      {Array.from({ length: 12 }, (_, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-full transition-all"
                          style={{
                            height: `${20 + Math.random() * 60}%`,
                            background: isPlaying ? colors[idx] : '#3F3F46',
                            opacity: isPlaying ? 0.8 : 0.4,
                          }}
                        />
                      ))}
                    </div>

                    {/* Select button */}
                    <button
                      onClick={() => handleSelect(sample.task_id)}
                      disabled={isSelected}
                      className={`
                        w-7 sm:w-8 h-7 sm:h-8 rounded-lg flex items-center justify-center flex-shrink-0
                        transition-all border
                        ${isSelected
                          ? 'bg-[#F5C842] border-[#F5C842] text-black'
                          : 'border-[#27272A] text-[#52525B] hover:border-[#F5C842] hover:text-[#F5C842]'
                        }
                      `}
                      data-testid={`sample-select-${idx}`}
                    >
                      <Check size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Cost savings hint */}
      {totalCount > 1 && (
        <p className="mt-2 text-[8px] sm:text-[9px] text-[#52525B] text-center">
          {totalCount} variaciones por solo {totalCount === 2 ? '3' : '5'} corcheas extra
        </p>
      )}
    </div>
  );
}

export default SamplePicker;
